/**
 * AppIcon — vendor-color initial tile for a connector / LLM provider / AI client.
 *
 * Brand colors are stable per vendor (Gmail red, Slack purple, Anthropic orange,
 * etc.) so users recognize the source at a glance even without the brand
 * wordmark. Renders a colored rounded square with 1-2 uppercase letter initials
 * derived from the display name. Warm-grey fallback for any unknown slug so
 * new vendors never break the layout.
 *
 * When to use: this is the fallback primitive — prefer <ConnectorLogo /> for
 * connectors (consumes `logo_url` from DB, falls back here on null) and
 * <BrandLogo /> for LLM providers + AI clients + any slug-keyed surface
 * (consumes a brand registry of simpleicons URLs, falls back here on unknown
 * slug). Use <AppIcon /> directly only when neither chain applies — i.e.
 * you're rendering a slug you know won't be in any registry.
 *
 * Sizes:
 *   - sm: 28px (inline lists, row leading icons)
 *   - md: 40px (default — tile card headers)
 *   - lg: 56px (page header anchor — lightning-highlight panels)
 *
 * Adding a new vendor: extend APP_COLORS below with the hex from the vendor's
 * brand guidelines. Don't pick a near-color by eye; check the actual brand
 * spec so the tile feels authentic to that vendor's identity.
 */

const APP_COLORS: Record<string, string> = {
  gmail:        '#EA4335',
  google_ads:   '#4285F4',
  gdrive:       '#1FA463',
  slack:        '#611F69',
  hubspot:      '#FF7A59',
  stripe:       '#635BFF',
  salesforce:   '#00A1E0',
  quickbooks:   '#2CA01C',
  linkedin:     '#0A66C2',
  apollo:       '#1D4ED8',
  notion:       '#1A1A17',
  ga4:          '#E37400',
  // Push #15 — AI client brand colors used by the Home "Use Olytic in your
  // favorite AI app" section. Falls back to grey for "other" / unknown.
  claude:       '#D97757',
  chatgpt:      '#10A37F',
  cursor:       '#1A1A1A',
  // Push #16 — LLM model provider brand colors. Used on Models list cards +
  // ModelDetail page headers per rule #2 (provider logo wins over generic
  // icon when a provider exists). Slugs match llm__provider_template.name.
  anthropic:    '#D97757',
  openai:       '#10A37F',
  google:       '#4285F4',
  bedrock:      '#FF9900',
  aws:          '#FF9900',
};

function colorFor(slug: string): string {
  return APP_COLORS[slug] ?? '#475467';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
  return `${first}${second ?? ''}`.toUpperCase().slice(0, 2);
}

interface AppIconProps {
  slug: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
}

export function AppIcon({ slug, name, size = 'md' }: AppIconProps) {
  const dim = size === 'lg' ? 56 : size === 'sm' ? 28 : 40;
  const fontSize = Math.round(dim * 0.38);
  const radius = size === 'sm' ? 8 : 10;
  return (
    <div
      className="tile-icon"
      style={{
        background: colorFor(slug),
        width: `${dim}px`,
        height: `${dim}px`,
        fontSize: `${fontSize}px`,
        borderRadius: `${radius}px`,
      }}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}
